import { useState, useEffect } from 'react';
import { Send, Save, Eye } from 'lucide-react';
import { RichTextEditor } from '../common/RichTextEditor';
import { AudienceSelector, AudienceType } from './AudienceSelector';
import { apiClient } from '../../lib/api';
import { toast } from '../../lib/toast';

interface BlastComposerProps {
  blastId?: string;
  onSaved?: () => void;
  onSent?: () => void;
}

interface PreviewData {
  count: number;
  recipients: { email: string; name: string; role: string }[];
}

export function BlastComposer({ blastId, onSaved, onSent }: BlastComposerProps) {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [audienceType, setAudienceType] = useState<AudienceType>('all');
  const [selectedAmIds, setSelectedAmIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [currentBlastId, setCurrentBlastId] = useState<string | undefined>(blastId);

  useEffect(() => {
    if (blastId) {
      loadBlast(blastId);
    }
  }, [blastId]);

  const loadBlast = async (id: string) => {
    try {
      const response = await apiClient.get<{ success: boolean; data: any }>(`/blasts/${id}`);
      if (response.success) {
        const blast = response.data;
        setSubject(blast.subject);
        setBody(blast.body || '');
        setAudienceType(blast.audienceType);
        setSelectedAmIds(blast.selectedAmIds?.map((id: any) => id.toString()) || []);
      }
    } catch (error) {
      toast.error('Failed to load blast');
    }
  };

  const handleSave = async () => {
    if (!subject.trim()) {
      toast.error('Subject is required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        subject,
        body,
        audienceType,
        selectedAmIds: audienceType === 'pods' ? selectedAmIds : undefined
      };

      if (currentBlastId) {
        await apiClient.put(`/blasts/${currentBlastId}`, payload);
        toast.success('Draft saved');
      } else {
        const response = await apiClient.post<{ success: boolean; data: any }>('/blasts', payload);
        if (response.success) {
          setCurrentBlastId(response.data._id);
          toast.success('Draft created');
        }
      }
      onSaved?.();
    } catch (error) {
      toast.error('Failed to save draft');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    // Save first if needed
    if (!currentBlastId) {
      await handleSave();
    }
    if (!currentBlastId && !subject.trim()) return;

    // Use the currentBlastId which may have been set by handleSave
    const idToPreview = currentBlastId;
    if (!idToPreview) return;

    try {
      const response = await apiClient.get<{ success: boolean; data: PreviewData }>(
        `/blasts/${idToPreview}/audience-preview`
      );
      if (response.success) {
        setPreview(response.data);
        setShowPreview(true);
      }
    } catch (error) {
      toast.error('Failed to preview audience');
    }
  };

  const handleSend = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error('Subject and body are required to send');
      return;
    }

    // Save first
    await handleSave();
    const idToSend = currentBlastId;
    if (!idToSend) return;

    setSending(true);
    try {
      const response = await apiClient.post<{ success: boolean; message: string }>(
        `/blasts/${idToSend}/send`
      );
      if (response.success) {
        toast.success('Blast is being sent!');
        setShowConfirm(false);
        // Reset form
        setSubject('');
        setBody('');
        setAudienceType('all');
        setSelectedAmIds([]);
        setCurrentBlastId(undefined);
        setPreview(null);
        onSent?.();
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to send blast');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Subject */}
      <div>
        <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
          Subject
        </label>
        <input
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Email subject line..."
          className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          style={{ borderColor: 'var(--border-primary)', background: 'var(--surface-primary)', color: 'var(--text-primary)' }}
        />
      </div>

      {/* Body */}
      <div>
        <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
          Email Body
        </label>
        <RichTextEditor
          content={body}
          onChange={setBody}
          placeholder="Compose your email..."
        />
      </div>

      {/* Audience */}
      <AudienceSelector
        audienceType={audienceType}
        selectedAmIds={selectedAmIds}
        onAudienceTypeChange={setAudienceType}
        onSelectedAmIdsChange={setSelectedAmIds}
      />

      {/* Preview Panel */}
      {showPreview && preview && (
        <div className="border rounded-lg p-4" style={{ borderColor: 'var(--border-primary)', background: 'var(--surface-secondary)' }}>
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
              Audience Preview ({preview.count} recipients)
            </h4>
            <button
              onClick={() => setShowPreview(false)}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              Close
            </button>
          </div>
          <div className="max-h-48 overflow-y-auto space-y-1">
            {preview.recipients.map((r, i) => (
              <div key={i} className="flex items-center justify-between text-sm py-1">
                <span style={{ color: 'var(--text-primary)' }}>{r.name}</span>
                <div className="flex items-center gap-2">
                  <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100" style={{ color: 'var(--text-tertiary)' }}>
                    {r.role === 'distributor' ? 'AM' : 'Physician'}
                  </span>
                  <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>{r.email}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-3 pt-2">
        <button
          onClick={handleSave}
          disabled={saving || !subject.trim()}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border transition-colors hover:bg-gray-50 disabled:opacity-50"
          style={{ borderColor: 'var(--border-primary)', color: 'var(--text-primary)' }}
        >
          <Save className="h-4 w-4" />
          {saving ? 'Saving...' : 'Save Draft'}
        </button>

        <button
          onClick={handlePreview}
          disabled={!subject.trim()}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border transition-colors hover:bg-gray-50 disabled:opacity-50"
          style={{ borderColor: 'var(--border-primary)', color: 'var(--text-primary)' }}
        >
          <Eye className="h-4 w-4" />
          Preview Recipients
        </button>

        <button
          onClick={() => setShowConfirm(true)}
          disabled={!subject.trim() || !body.trim()}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg text-white transition-colors disabled:opacity-50"
          style={{ backgroundColor: '#3B82F6' }}
        >
          <Send className="h-4 w-4" />
          Send Blast
        </button>
      </div>

      {/* Confirm Dialog */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="rounded-xl shadow-xl p-6 max-w-md w-full mx-4" style={{ background: 'var(--surface-primary)' }}>
            <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
              Confirm Send
            </h3>
            <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
              Are you sure you want to send this blast? This action cannot be undone.
            </p>
            <p className="text-sm mb-4" style={{ color: 'var(--text-tertiary)' }}>
              Subject: <strong>{subject}</strong>
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowConfirm(false)}
                className="px-4 py-2 text-sm font-medium rounded-lg border transition-colors hover:bg-gray-50"
                style={{ borderColor: 'var(--border-primary)', color: 'var(--text-primary)' }}
              >
                Cancel
              </button>
              <button
                onClick={handleSend}
                disabled={sending}
                className="px-4 py-2 text-sm font-medium rounded-lg text-white transition-colors disabled:opacity-50"
                style={{ backgroundColor: '#3B82F6' }}
              >
                {sending ? 'Sending...' : 'Send Now'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
